<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import {
  UiDialog,
  UiButton,
  UiInputField,
  UiRichTextEditor,
  UiSelect,
  UiText,
  UiInfoBox,
  useApi,
} from '@shopexperts/shared';
import { useQueryClient } from '@tanstack/vue-query';
import type { AdminSetting } from '@shopexperts/legacy-api';
import { renderInviteTemplate } from '@/app/utils/inviteEmailTemplates';
import { useAdminExpertDetail } from '@/app/composables/expert-manager/useAdminExpertDetail';
import {
  useAdminEmailFromProfiles,
  useAdminEmailReplyTo,
  useSendAdminEmail,
} from '@/app/composables/expert-manager/useAdminEmails';
import { expertProfileUrl } from './expertSlug';

const props = defineProps<{ expertId: number }>();

const open = defineModel<boolean>({ default: false });

const EXPERT_TAG = 'unclaimed:invited';
const EMAIL_TAG = 'unclaimed-invite';
const DEFAULT_FROM_PROFILE = 'support';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const { expert, error: expertError, refetch: refetchExpert } = useAdminExpertDetail(computed(() => props.expertId));
const { profiles } = useAdminEmailFromProfiles(() => open.value);
const { replyTo } = useAdminEmailReplyTo();
const fromHint = computed(() =>
  replyTo.value ? `Replies go to ${replyTo.value}.` : 'Replies go to the configured reply-to address.',
);
const { sendEmail } = useSendAdminEmail();
const isSending = computed(() => sendEmail.isPending.value);

const to = ref('');
const from = useLocalStorage('admin-invite-email:from', DEFAULT_FROM_PROFILE);
const subject = ref('');
const body = ref('');

const fromOptions = computed(() =>
  profiles.value.map((profile) => ({
    value: profile.key,
    label: `${profile.name} — ${profile.address}`,
  })),
);

watch(profiles, (list) => {
  const first = list[0];
  if (first && !list.some((profile) => profile.key === from.value)) {
    from.value = first.key;
  }
});

const api = useApi();
const queryClient = useQueryClient();
const templateSettings = ref<AdminSetting[] | null>(null);
const prefillError = ref('');
const needsPrefill = ref(false);
const isLoadingTemplate = ref(false);
const fieldsDisabled = computed(() => isSending.value || needsPrefill.value);
const loadError = computed(() => prefillError.value || (needsPrefill.value && expertError.value ? 'Failed to load the expert. Retry to load the invitation.' : ''));
let loadId = 0;

function applyPrefill() {
  const exp = expert.value;
  if (!needsPrefill.value || !open.value || !templateSettings.value || !exp || exp.id !== props.expertId) return;
  try {
    const rendered = renderInviteTemplate(templateSettings.value, exp, exp.slug ? expertProfileUrl(exp.listing_type, exp.slug) : '');
    to.value = exp.email ?? '';
    subject.value = rendered.subject;
    body.value = rendered.body;
    prefillError.value = '';
    needsPrefill.value = false;
  } catch (error) {
    prefillError.value = error instanceof Error ? error.message : 'Failed to prepare invitation.';
  }
}

async function loadTemplate() {
  const currentLoad = ++loadId;
  needsPrefill.value = true;
  isLoadingTemplate.value = true;
  prefillError.value = '';
  templateSettings.value = null;
  to.value = '';
  subject.value = '';
  body.value = '';
  if (expertError.value) void refetchExpert();
  try {
    const settings = await queryClient.fetchQuery({
      queryKey: ['admin', 'settings', 'admin_emails'],
      queryFn: () => api.admin.settings.list({ query: { groups: ['admin_emails'] } }),
      staleTime: 0,
    });
    if (currentLoad !== loadId || !open.value) return;
    templateSettings.value = settings;
    applyPrefill();
  } catch {
    if (currentLoad === loadId) prefillError.value = 'Failed to load invitation settings. Please retry.';
  } finally {
    if (currentLoad === loadId) isLoadingTemplate.value = false;
  }
}

watch([open, () => props.expertId], ([isOpen]) => {
  if (isOpen) void loadTemplate();
  else ++loadId;
}, { immediate: true });
watch(expert, applyPrefill);

const lengthError = computed(() => subject.value.trim().length > 255
  ? 'Subject must be 255 characters or fewer.'
  : body.value.length > 65_000 ? 'Message must be 65,000 characters or fewer.' : '');
const bodyText = computed(() => body.value.replace(/<[^>]*>/g, ' ').trim());
const canSend = computed(
  () =>
    !needsPrefill.value && !loadError.value && !lengthError.value &&
    EMAIL_PATTERN.test(to.value.trim()) &&
    profiles.value.some((profile) => profile.key === from.value) &&
    !!subject.value.trim() &&
    !!bodyText.value,
);

async function send() {
  if (!canSend.value || isSending.value) return;
  try {
    await sendEmail.mutateAsync({
      to: to.value.trim(),
      from: from.value,
      subject: subject.value.trim(),
      body: body.value,
      tags: [EMAIL_TAG],
      reply_to_support: true,
      expert_id: props.expertId,
      expert_tags: [EXPERT_TAG],
    });
    open.value = false;
  } catch {
    // Toast shown by the mutation; keep the dialog open so nothing typed is lost.
  }
}

function cancel() {
  open.value = false;
}
</script>

<template>
  <UiDialog v-model="open" title="Send invite email" size="lg">
    <div class="flex flex-col gap-5">
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        :text="`Invite ${expert?.display_name ?? 'this expert'} to claim their listing. Review the prefilled details before sending.`"
      />

      <UiInfoBox v-if="loadError" variant="error" title="Invitation unavailable">
        <UiText as="p" variant="p-small" :text="loadError" />
        <UiButton label="Retry" variant="outline" :disabled="isLoadingTemplate" @click="loadTemplate" />
      </UiInfoBox>
      <UiText v-else-if="needsPrefill" as="p" variant="p-small" text="Loading invitation…" />
      <UiText v-if="lengthError" as="p" variant="p-small" class="text-red-600" :text="lengthError" />

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="To" />
        <UiInputField
          v-model="to"
          type="email"
          placeholder="name@example.com"
          :disabled="fieldsDisabled"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="From" />
        <UiSelect
          v-model="from"
          :options="fromOptions"
          placeholder="Select sender"
          :hint="fromHint"
          :disabled="fieldsDisabled"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Subject" />
        <UiInputField v-model="subject" :disabled="fieldsDisabled" />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Message" />
        <UiRichTextEditor v-model="body" :rows="10" resize="vertical" :disabled="fieldsDisabled" link-follow-controls />
        <UiText
          as="p"
          variant="caption-normal"
          class="text-se-gray-500"
          text="“Warm Regards, The Shopexperts Team” is appended automatically — no need to sign off."
        />
      </div>

      <UiText
        as="p"
        variant="caption-normal"
        class="text-se-gray-500"
        :text="`Sending also tags the expert with “${EXPERT_TAG}”.`"
      />
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <UiButton variant="outline" label="Cancel" :disabled="isSending" @click="cancel" />
        <UiButton
          variant="primary"
          :label="isSending ? 'Sending…' : 'Send email'"
          :disabled="isSending || !canSend"
          @click="send"
        />
      </div>
    </template>
  </UiDialog>
</template>
